import type { Review } from "../types";

function createMockReview(overrides: Partial<Review> & Pick<Review, "id" | "date" | "content_text" | "customer_name" | "customer_headline">): Review {
	return {
		workspace_id: "mock-workspace",
		form_id: null,
		form: null,
		type: "testimonial",
		rating: 5,
		content_type: "text",
		private_feedback: null,
		consent: "public",
		video_url: null,
		video_thumbnail_url: null,
		video_duration: null,
		video_file_size: null,
		video_status: null,
		mux_upload_id: null,
		mux_asset_id: null,
		mux_playback_id: null,
		audio_url: null,
		audio_duration: null,
		customer_avatar_url: null,
		customer_email: "",
		customer_phone: null,
		customer_company_name: overrides.customer_company_name ?? "",
		customer_job_title: overrides.customer_job_title ?? "",
		customer_company_website_url: null,
		customer_company_logo_url: null,
		source: "manual",
		source_post_url: null,
		status: "approved",
		published_at: overrides.date,
		tags: [],
		answers: [],
		created_by_id: "",
		created_at: overrides.date,
		updated_by_id: "",
		updated_at: overrides.date,
		deleted_by_id: null,
		deleted_at: null,
		...overrides,
	};
}

export function getMockReviews(currentBrandName?: string): Review[] {
	const brandName = currentBrandName?.trim() || "your brand";

	return [
		createMockReview({
			id: "mock-review-1",
			date: "2026-01-10",
			rating: 4,
			content_text: `${brandName} helped us collect social proof in less than a day. We dropped the testimonial widget into our site and started seeing stronger conversion signals right away.`,
			customer_name: "Sarah Mitchell",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-f1.jpg",
			customer_headline: "Head of Marketing at Pixelforge",
			customer_company_name: "Pixelforge",
			customer_job_title: "Head of Marketing",
			source: "form",
		}),
		createMockReview({
			id: "mock-review-2",
			date: "2026-01-05",
			rating: 5,
			content_text: `The review moderation workflow in ${brandName} is simple and fast. Our team can approve testimonials in minutes and keep our wall up to date without extra effort.`,
			customer_name: "James Rodriguez",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-m1.jpg",
			customer_headline: "Engineering Manager at Paystream",
			customer_company_name: "Paystream",
			customer_job_title: "Engineering Manager",
			source: "google_reviews",
		}),
		createMockReview({
			id: "mock-review-3",
			date: "2025-12-28",
			type: "feedback",
			rating: 3,
			content_text: "Switching from screenshots to real testimonials helped a little, but setup and moderation took longer than we expected. It works, though we still need to spend time each week keeping things organized.",
			customer_name: "Priya Patel",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-f3.jpg",
			customer_headline: "Operations Lead at Notewise",
			customer_company_name: "Notewise",
			customer_job_title: "Operations Lead",
			source: "capterra",
		}),
		createMockReview({
			id: "mock-review-4",
			date: "2025-12-22",
			rating: 4,
			content_text: "",
			content_type: "video",
			video_url: "https://stream.mux.com/SsR4Z8qqK97vnVG8FSJvtouz2yCW0002Z2glxaswC00Ttc.m3u8",
			video_thumbnail_url: "https://image.mux.com/SsR4Z8qqK97vnVG8FSJvtouz2yCW0002Z2glxaswC00Ttc/thumbnail.jpg",
			mux_playback_id: "SsR4Z8qqK97vnVG8FSJvtouz2yCW0002Z2glxaswC00Ttc",
			video_status: "ready",
			video_duration: 8,
			customer_name: "Liam Carter",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-m2.jpg",
			customer_headline: "Customer Success Lead at CloudMint",
			customer_company_name: "CloudMint",
			customer_job_title: "Customer Success Lead",
			source: "instagram",
		}),
		createMockReview({
			id: "mock-review-5",
			date: "2025-12-15",
			rating: 4,
			content_text: "",
			content_type: "audio",
			audio_url: "/audio/mock-reviews/customer-audio-f2.mp3",
			customer_name: "Emma Johnson",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-f2.jpg",
			customer_headline: "Product Designer at Launchgrid",
			customer_company_name: "Launchgrid",
			customer_job_title: "Product Designer",
			source: "facebook",
		}),
		createMockReview({
			id: "mock-review-6",
			date: "2025-12-08",
			rating: 5,
			content_text: "Having all reviews in one place makes campaign planning much easier. We can quickly pull the right testimonials for each audience segment.",
			customer_name: "Noah Kim",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-m3.jpg",
			customer_headline: "Growth Manager at Brightlane",
			customer_company_name: "Brightlane",
			customer_job_title: "Growth Manager",
			source: "email",
		}),
		createMockReview({
			id: "mock-review-7",
			date: "2025-12-01",
			rating: 4,
			content_text: "The moderation controls give us confidence to publish faster without worrying about quality. It reduced manual back-and-forth across teams.",
			customer_name: "Olivia Chen",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-f4.jpg",
			customer_headline: "Content Lead at FinchWorks",
			customer_company_name: "FinchWorks",
			customer_job_title: "Content Lead",
			source: "linkedin",
		}),
		createMockReview({
			id: "mock-review-8",
			date: "2025-11-24",
			rating: 5,
			content_text: "We replaced static quotes with real customer stories and saw stronger engagement on pricing and feature pages within the same week.",
			customer_name: "Ethan Walker",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-m4.jpg",
			customer_headline: "Revenue Operations at ScalePilot",
			customer_company_name: "ScalePilot",
			customer_job_title: "Revenue Operations",
			source: "form",
		}),
		createMockReview({
			id: "mock-review-9",
			date: "2025-11-16",
			rating: 4,
			content_text: "The tagging workflow helps us organize testimonials by industry and use case, which makes repurposing content for ads much faster.",
			customer_name: "Ava Thompson",
			customer_avatar_url: "/images/mock-reviews/customer-avatar-f5.jpg",
			customer_headline: "Demand Gen Manager at Northpeak",
			customer_company_name: "Northpeak",
			customer_job_title: "Demand Gen Manager",
			source: "twitter",
		}),
	];
}
